<template>
    <base-dialog :value="dialog" :title="title" :width="420" @input="onInput" @close="hide">
        <template v-if="!editing">
            <div class="form-group mb-5">
                <span class="form-label">{{ $t('business.currency') }}</span>
                <kimsa-combobox :items="currencyOptions" :value="currency" item-text="name" item-value="value"
                    no-object dense solo @change="$emit('change:currency', $event)" />
                <span class="form-hint">{{ $t('business.currency_hint') }}</span>
            </div>

            <div class="plan-list">
                <button v-for="plan in planTypes" :key="plan.id" type="button" class="plan-row" @click="edit(plan)">
                    <span class="plan-dot" :style="{ background: plan.color }" />
                    <span class="plan-info">
                        <span class="plan-name">{{ plan.name }}</span>
                        <span class="plan-meta">
                            {{ formatMoney(plan.amount) }}{{ $t('business.per_month_short') }} ·
                            {{ $tc('business.clients_count', usage(plan.id), { n: usage(plan.id) }) }}{{ frequencyMeta(plan) }}
                        </span>
                    </span>
                    <v-icon size="15">mdi-pencil-outline</v-icon>
                </button>

                <span v-if="planTypes.length === 0" class="plan-empty">{{ $t('business.no_plan_types') }}</span>
            </div>
        </template>

        <template v-else>
            <div class="form-group">
                <span class="form-label">{{ $t('common.name') }}</span>
                <kimsa-text-field :placeholder="$t('business.plan_placeholder')" :value="name" @change="name = $event"
                    dense force-value />
            </div>

            <div class="form-group mt-4">
                <span class="form-label">{{ $t('business.default_amount') }}</span>
                <kimsa-text-field placeholder="80" :value="amount" @change="amount = $event" input-mode="numeric" dense
                    force-value />
            </div>

            <div class="form-group mt-4">
                <span class="form-label">{{ $t('business.color') }}</span>
                <div class="color-swatches">
                    <button v-for="option in palette" :key="option" type="button" class="color-swatch"
                        :class="{ selected: option === color }" :style="{ background: option }"
                        @click="color = option" />
                </div>
            </div>

            <div class="form-group mt-4">
                <span class="form-label">{{ $t('business.frequency') }}</span>
                <span class="form-hint">{{ $t('business.frequency_hint') }}</span>
                <v-row dense>
                    <v-col cols="6">
                        <span class="form-sublabel">{{ $t('business.frequency_weekly') }}</span>
                        <kimsa-combobox :items="weeklyOptions" :value="weekly" item-text="name" item-value="value"
                            no-object dense solo @change="weekly = $event" />
                    </v-col>
                    <v-col cols="6">
                        <span class="form-sublabel">{{ $t('business.frequency_monthly') }}</span>
                        <kimsa-combobox :items="monthlyOptions" :value="monthly" item-text="name" item-value="value"
                            no-object dense solo @change="monthly = $event" />
                    </v-col>
                </v-row>
                <span class="form-hint" :class="{ 'plan-warning-text': frequencyConflict }">{{ frequencySummary }}</span>
            </div>

            <div class="form-group mt-4">
                <span class="form-label">{{ $t('business.included_activities') }}</span>
                <span class="form-hint mb-2">{{ $t('business.included_activities_hint') }}</span>
                <access-rules-editor :rules="rules" :activities="activities"
                    :empty-text="$t('business.no_included_activities')" :add-label="$t('business.add_activity_rule')"
                    @change="rules = $event" />
            </div>

            <span v-if="blockedText" class="plan-warning">{{ blockedText }}</span>
        </template>

        <template v-slot:footer>
            <dialog-actions v-if="!editing" hide-cancel :confirm-label="$t('business.new_plan_type')"
                @confirm="edit(null)" />

            <dialog-actions v-else :confirm-label="confirmLabel" :disabled="!valid" @cancel="back" @confirm="save">
                <template v-slot:extra>
                    <button v-if="isEdit" type="button" class="plan-delete" :disabled="!!blockedText" @click="remove">
                        {{ $t('common.delete') }}
                    </button>
                </template>
            </dialog-actions>
        </template>
        <kimsa-confirm ref="deleteConfirm" :title="$t('business.delete_plan_type')"
            :content="$t('business.delete_plan_type_body', { name: name })" :persistent="false" alert-mode
            show-cancel @confirm="onConfirmDelete" />
    </base-dialog>
</template>

<script>
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import AccessRulesEditor from "@/components/company/business/AccessRulesEditor";
import {
    ACTIVITY_PALETTE, CURRENCIES, DEFAULT_CURRENCY, FREQUENCY_MONTHLY_MAX, FREQUENCY_WEEKLY_MAX, formatMoney, newId,
    normalizeFrequency, normalizeRules, parseAmount,
} from "@/utils/Business";

export default {
    name: "PlanTypesDialog",
    components: { BaseDialog, DialogActions, KimsaTextField, KimsaCombobox, KimsaConfirm, AccessRulesEditor },
    props: {
        planTypes: {
            type: Array,
            default: () => [],
        },
        clients: {
            type: Array,
            default: () => [],
        },
        currency: {
            type: String,
            default: DEFAULT_CURRENCY,
        },
        activities: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            dialog: false,
            editing: false,
            plan: null,
            name: '',
            amount: '',
            color: ACTIVITY_PALETTE[0],
            palette: ACTIVITY_PALETTE,
            rules: [],
            weekly: 0,
            monthly: 0,
        }
    },
    computed: {
        isEdit(vm = this) {
            return !!vm.plan?.id
        },
        currencyOptions() {
            return CURRENCIES.map(item => ({ value: item.code, name: `${item.code} (${item.symbol})` }))
        },
        title(vm = this) {
            if (!vm.editing) return vm.$t('business.plan_types')
            return vm.isEdit ? vm.$t('business.edit_plan_type') : vm.$t('business.new_plan_type')
        },
        confirmLabel(vm = this) {
            return vm.isEdit ? vm.$t('common.save') : vm.$t('common.create')
        },
        valid(vm = this) {
            return !!String(vm.name || '').trim()
        },
        weeklyOptions(vm = this) {
            return vm.frequencyOptions(FREQUENCY_WEEKLY_MAX)
        },
        monthlyOptions(vm = this) {
            return vm.frequencyOptions(FREQUENCY_MONTHLY_MAX)
        },
        frequency(vm = this) {
            return normalizeFrequency({ weekly: vm.weekly, monthly: vm.monthly })
        },
        frequencyConflict(vm = this) {
            const { weekly, monthly } = vm.frequency
            return !!(weekly && monthly && monthly < weekly)
        },
        frequencySummary(vm = this) {
            const { weekly, monthly } = vm.frequency
            if (vm.frequencyConflict) return vm.$t('business.frequency_conflict')
            if (weekly && monthly) return vm.$t('business.frequency_both', { weekly, monthly })
            if (weekly) return vm.$tc('business.frequency_weekly_only', weekly, { weekly })
            if (monthly) return vm.$tc('business.frequency_monthly_only', monthly, { monthly })
            return vm.$t('business.frequency_none')
        },
        blockedText(vm = this) {
            if (!vm.isEdit) return ''
            const count = vm.usage(vm.plan.id)
            if (count === 0) return ''
            return vm.$tc('business.plan_in_use', count, { n: count })
        },
    },
    methods: {
        formatMoney,
        frequencyOptions(max) {
            const vm = this
            return [
                { value: 0, name: vm.$t('business.no_limit') },
                ...Array.from({ length: max }, (_, index) => index + 1)
                    .map(n => ({ value: n, name: vm.$tc('business.shifts_count', n, { n }) })),
            ]
        },
        frequencyMeta(plan) {
            const vm = this
            const { weekly, monthly } = normalizeFrequency(plan?.frequency)
            const parts = []
            if (weekly) parts.push(`${weekly}${vm.$t('business.per_week_short')}`)
            if (monthly) parts.push(`${monthly}${vm.$t('business.per_month_short')}`)
            return parts.map(part => ` · ${part}`).join('')
        },
        usage(planId) {
            return this.clients.filter(client => client.planId === planId).length
        },
        show() {
            const vm = this
            vm.editing = false
            vm.plan = null
            vm.dialog = true
        },
        hide() {
            this.dialog = false
        },
        onInput(value) {
            if (!value) this.hide()
        },
        back() {
            this.editing = false
        },
        edit(plan) {
            const vm = this
            vm.plan = plan
            vm.name = plan?.name || ''
            vm.amount = plan?.amount || ''
            vm.color = plan?.color || ACTIVITY_PALETTE[0]
            vm.rules = (plan?.activities || []).map(rule => ({ ...rule, days: [...rule.days] }))
            const frequency = normalizeFrequency(plan?.frequency)
            vm.weekly = frequency.weekly || 0
            vm.monthly = frequency.monthly || 0
            vm.editing = true
        },
        save() {
            const vm = this
            if (!vm.valid) return
            const plan = {
                id: vm.plan?.id || newId(),
                name: String(vm.name).trim(),
                amount: parseAmount(vm.amount),
                color: vm.color,
                activities: normalizeRules(vm.rules),
                frequency: vm.frequency,
            }
            const planTypes = [...vm.planTypes]
            const index = planTypes.findIndex(item => item.id === plan.id)
            if (index >= 0) planTypes.splice(index, 1, plan)
            else planTypes.push(plan)
            vm.$emit('change', planTypes)
            vm.back()
        },
        remove() {
            if (this.blockedText) return
            this.$refs.deleteConfirm.show()
        },
        onConfirmDelete() {
            const vm = this
            vm.$emit('change', vm.planTypes.filter(item => item.id !== vm.plan.id))
            vm.back()
        },
    },
}
</script>

<style scoped>
.plan-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.plan-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    text-align: left;
    color: var(--v-txtSecondary-base);
    transition: border-color 0.15s;
}

.plan-row:hover {
    border-color: var(--v-outline2-base);
}

.plan-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex-shrink: 0;
}

.plan-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
}

.plan-name {
    font-size: var(--fs-label);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.plan-meta {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.plan-empty {
    padding: 18px 0;
    text-align: center;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-label {
    font-size: var(--fs-label);
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.form-hint {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.form-sublabel {
    display: block;
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.plan-warning-text {
    color: var(--v-error-base);
}

.color-swatches {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.color-swatch {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid transparent;
    transition: border-color 0.15s, transform 0.15s;
}

.color-swatch.selected {
    border-color: var(--v-txtPrimary-base);
    transform: scale(1.12);
}

.plan-warning {
    display: block;
    margin-top: 14px;
    font-size: 11px;
    color: var(--v-error-base);
}

.plan-delete {
    padding: 0 20px;
    height: 52px;
    border-right: 1px solid var(--v-outline-base);
    font-size: var(--fs-body);
    font-weight: 500;
    color: var(--v-error-base);
}

.plan-delete:disabled {
    opacity: 0.4;
    cursor: default;
}
</style>
